import { Suspense } from 'react'

import { createFileRoute, retainSearchParams } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { z } from 'zod'

import { ActionsTab } from '@/features/activity/components/tabs/actions'
import { activityQueries } from '@/features/activity/queries'
import { ACTIVITY_STATUS } from '@/features/activity/types'
import { RouteErrorComponent } from '@/features/app-shell/route-error-component'

import { getCurrentTenant } from '@/shared/auth/store'
import { TableFullSkeleton } from '@/shared/data-table/components/table-skeleton'

import { ErrorBoundary } from '@/components/common/error-boundary'

const searchSchema = z.object({
	q: z.string().optional(),
	filters: z
		.object({
			status: ACTIVITY_STATUS.optional(),
			type: z.enum(['playbook', 'standalone']).optional(),
		})
		.optional(),
	action: z.string().optional(),
})

export const Route = createFileRoute('/_dashboard/activity/actions')({
	validateSearch: zodValidator(searchSchema),

	search: {
		middlewares: [retainSearchParams(['q', 'filters', 'action'])],
	},

	loaderDeps: ({ search }) => {
		let parsedType: 'workflow' | 'adhoc' = 'adhoc'

		if (search.filters?.type === 'playbook') {
			parsedType = 'workflow'
		}

		return { ...search, filters: { ...search.filters, type: parsedType } }
	},

	loader: async ({ context: { queryClient }, deps }) => {
		const tenant = getCurrentTenant() as string

		if (tenant) {
			if (deps.action) {
				queryClient.prefetchQuery(activityQueries.detail(tenant, deps.action))
			}

			await queryClient.ensureInfiniteQueryData(
				activityQueries.list(tenant, deps.filters.status, deps.filters.type),
			)
		}
	},

	head: () => ({
		meta: [
			{
				title: 'Activity | Actions | BitLyft AIR®',
			},
		],
	}),

	component: RouteComponent,
	pendingComponent: RoutePendingComponent,
	errorComponent: RouteErrorComponent,
})

/**
 * Actions Activity Route
 */
function RouteComponent() {
	return (
		<ErrorBoundary feature="activity" section="actions">
			<Suspense
				fallback={<TableFullSkeleton rows={15} withFilters withLastUpdated />}
			>
				<ActionsTab />
			</Suspense>
		</ErrorBoundary>
	)
}

/**
 * Loading state for Actions Activity
 */
function RoutePendingComponent() {
	return <TableFullSkeleton rows={15} withFilters withLastUpdated />
}
